function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function toAmount(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function buildLineKey(line) {
  const code = String(line?.code || '').trim();
  const name = normalizeText(line?.name);
  return code || name;
}

function compareRows(a, b) {
  const codeA = String(a.code || '');
  const codeB = String(b.code || '');
  if (codeA && codeB) {
    return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
  }
  return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
}

function sumRows(rows, amountKey) {
  return rows.reduce((total, row) => total + toAmount(row[amountKey]), 0);
}

const LABELS = {
  tr: {
    reportTitle: 'Kar veya Zarar ve Diger Kapsamli Gelir Tablosu',
    statementName: 'IFRS Kar veya Zarar ve Diger Kapsamli Gelir Tablosu',
    lineItem: 'Gelir Tablosu Kalemi',
    noteRef: 'Dipnot Ref.',
    currentPeriod: 'Cari Donem',
    previousPeriod: 'Onceki Donem',
    noComparative: 'Karsilastirmali onceki donem verisi bulunamadi.',
    basisNote: 'Sunum, IAS 1 ve IFRS 18 cizgisine uygun olarak kar veya zarar bolumu ile diger kapsamli gelir bolumu seklinde duzenlendi.',
    profitLossSection: 'KAR VEYA ZARAR',
    ociSection: 'DIGER KAPSAMLI GELIR',
    revenue: 'Hasilat',
    revenueDeductions: 'Hasilat Indirimleri',
    netRevenue: 'NET HASILAT',
    costOfSales: 'Satilanlarin Maliyeti',
    grossProfit: 'BRUT KAR / ZARAR',
    otherOperatingIncome: 'Diger Faaliyet ve Finansman Gelirleri',
    operatingExpenses: 'Faaliyet Giderleri',
    otherOperatingExpenses: 'Diger Faaliyet Gider ve Zararlari',
    operatingProfit: 'FAALIYET KARI / ZARARI',
    financeCosts: 'Finansman Giderleri',
    nonOperatingIncome: 'Diger Olagandisi Gelir ve Karlar',
    nonOperatingExpenses: 'Diger Olagandisi Gider ve Zararlar',
    profitBeforeTax: 'VERGI ONCESI KAR / ZARAR',
    taxExpense: 'Vergi Gideri',
    profitForPeriod: 'DONEM KARI / ZARARI',
    ociItems: 'Diger Kapsamli Gelir Kalemleri',
    totalOci: 'DIGER KAPSAMLI GELIR TOPLAMI',
    totalComprehensiveIncome: 'TOPLAM KAPSAMLI GELIR',
    totalProfitLoss: 'KAR / ZARAR TOPLAMI'
  },
  en: {
    reportTitle: 'Statement of Profit or Loss and Other Comprehensive Income',
    statementName: 'IFRS Statement of Profit or Loss and Other Comprehensive Income',
    lineItem: 'Statement Line Item',
    noteRef: 'Note Ref.',
    currentPeriod: 'Current Period',
    previousPeriod: 'Previous Period',
    noComparative: 'No comparative previous-period data was found.',
    basisNote: 'Presentation is structured in profit or loss and other comprehensive income sections in line with IAS 1 and IFRS 18.',
    profitLossSection: 'PROFIT OR LOSS',
    ociSection: 'OTHER COMPREHENSIVE INCOME',
    revenue: 'Revenue',
    revenueDeductions: 'Revenue Deductions',
    netRevenue: 'NET REVENUE',
    costOfSales: 'Cost of Sales',
    grossProfit: 'GROSS PROFIT / LOSS',
    otherOperatingIncome: 'Other Operating and Finance Income',
    operatingExpenses: 'Operating Expenses',
    otherOperatingExpenses: 'Other Operating Expenses and Losses',
    operatingProfit: 'OPERATING PROFIT / LOSS',
    financeCosts: 'Finance Costs',
    nonOperatingIncome: 'Other Non-operating Income',
    nonOperatingExpenses: 'Other Non-operating Expenses',
    profitBeforeTax: 'PROFIT / LOSS BEFORE TAX',
    taxExpense: 'Income Tax Expense',
    profitForPeriod: 'PROFIT / LOSS FOR THE PERIOD',
    ociItems: 'Other Comprehensive Income Items',
    totalOci: 'TOTAL OTHER COMPREHENSIVE INCOME',
    totalComprehensiveIncome: 'TOTAL COMPREHENSIVE INCOME',
    totalProfitLoss: 'TOTAL PROFIT OR LOSS'
  }
};

const LINE_LABELS_EN = {
  DOMESTIC_SALES: 'Domestic Sales',
  EXPORT_SALES: 'Export Sales',
  OTHER_REVENUE: 'Other Revenue',
  SALES_RETURNS: 'Sales Returns',
  SALES_DISCOUNTS: 'Sales Discounts',
  OTHER_SALES_DEDUCTIONS: 'Other Sales Deductions',
  COST_OF_GOODS_SOLD: 'Cost of Finished Goods Sold',
  COST_OF_MERCHANDISE_SOLD: 'Cost of Merchandise Sold',
  COST_OF_SERVICES_SOLD: 'Cost of Services Sold',
  OTHER_COST_OF_SALES: 'Other Cost of Sales',
  INVENTORY_WRITE_DOWN_EXPENSE: 'Inventory Write-Down Expense',
  RESEARCH_EXPENSE: 'Research and Development Expenses',
  MARKETING_EXPENSE: 'Marketing, Selling and Distribution Expenses',
  ADMIN_EXPENSE: 'General Administrative Expenses',
  DEPRECIATION_ADJUSTMENT_EXPENSE: 'Depreciation Adjustment Expense',
  DEPRECIATION_ROU: 'Right-of-Use Asset Depreciation',
  DIVIDEND_INCOME: 'Dividend Income from Associates',
  SUBSIDIARY_DIVIDEND_INCOME: 'Dividend Income from Subsidiaries',
  OTHER_OPERATING_INCOME: 'Other Operating Income',
  COMMISSION_INCOME: 'Commission Income',
  FOREIGN_EXCHANGE_GAIN: 'Foreign Exchange Gains',
  REDISCOUNT_INTEREST_INCOME: 'Rediscount Interest Income',
  SECURITIES_SALE_GAIN: 'Gains on Sale of Securities',
  SECURITIES_FAIR_VALUE_GAIN: 'Fair Value Gains on Securities',
  PRECIOUS_METALS_SALE_GAIN: 'Gains on Sale of Precious Metals',
  OTHER_ORDINARY_INCOME: 'Other Ordinary Income',
  DEPRECIATION_ADJUSTMENT_INCOME: 'Depreciation Adjustment Income',
  COMMISSION_EXPENSE: 'Commission Expense',
  PROVISION_EXPENSE: 'Provision Expense',
  SECURITIES_SALE_LOSS: 'Losses on Sale of Securities',
  SECURITIES_IMPAIRMENT_LOSS: 'Impairment Losses on Securities',
  PRECIOUS_METALS_SALE_LOSS: 'Losses on Sale of Precious Metals',
  FOREIGN_EXCHANGE_LOSS: 'Foreign Exchange Losses',
  REDISCOUNT_INTEREST_EXPENSE: 'Rediscount Interest Expense',
  TAX_DUTY_CHARGE: 'Taxes, Duties and Charges',
  OTHER_OPERATING_LOSS: 'Other Operating Expenses and Losses',
  OTHER_ORDINARY_EXPENSE: 'Other Ordinary Expenses',
  SHORT_TERM_FINANCE_EXPENSE: 'Short-Term Borrowing Costs',
  LONG_TERM_FINANCE_EXPENSE: 'Long-Term Borrowing Costs',
  FINANCE_EXPENSE_PROVISION: 'Finance Expense Provision',
  CARD_COMMISSION_EXPENSE: 'Credit Card Commission Expense',
  OTHER_FINANCE_EXPENSE: 'Other Finance Expenses',
  INTEREST_LEASE: 'Lease Interest Expense',
  EXTRAORDINARY_INCOME: 'Non-Operating Income and Gains',
  PRIOR_PERIOD_INCOME: 'Prior Period Income and Gains',
  OTHER_EXTRAORDINARY_INCOME: 'Other Non-Operating Income and Gains',
  OTHER_NON_OPERATING_INCOME: 'Other Non-Operating Income',
  EXTRAORDINARY_EXPENSE: 'Non-Operating Expenses and Losses',
  PRIOR_PERIOD_EXPENSE: 'Prior Period Expenses and Losses',
  OTHER_EXTRAORDINARY_EXPENSE: 'Other Non-Operating Expenses and Losses',
  OTHER_NON_OPERATING_EXPENSE: 'Other Non-Operating Expenses',
  CURRENT_TAX_EXPENSE: 'Current Tax Expense',
  DEFERRED_TAX_EXPENSE_VUK: 'Deferred Tax Expense / Income',
  DEFERRED_TAX_EXPENSE: 'Deferred Tax Expense / Income',
  OCI: 'Other Comprehensive Income'
};

function displayLineName(line, isTr) {
  const code = String(line?.code || '').trim();
  if (!isTr && LINE_LABELS_EN[code]) {
    return LINE_LABELS_EN[code];
  }
  return line?.name || code || '-';
}

function inferBucket(line) {
  const type = String(line?.accountType || '').trim().toUpperCase();
  const code = String(line?.code || '').trim();
  const normalizedCode = code.toUpperCase();
  const name = normalizeText(line?.name);

  if (
    type === 'OCI'
    || code.startsWith('9')
    || name.includes('comprehensive income')
    || name.includes('kapsamli gelir')
    || normalizedCode.includes('OCI')
  ) {
    return 'ociItems';
  }

  if (type === 'REVENUE') return 'revenue';
  if (type === 'REVENUE_DEDUCTION') return 'revenueDeductions';
  if (type === 'COST_OF_SALES') return 'costOfSales';
  if (type === 'OPERATING_EXPENSE') return 'operatingExpenses';
  if (type === 'OTHER_OPERATING' || type === 'OTHER_OPERATING_INCOME') return 'otherOperatingIncome';
  if (type === 'OTHER_OPERATING_EXPENSE') return 'otherOperatingExpenses';
  if (type === 'NON_OPERATING_INCOME') return 'nonOperatingIncome';
  if (type === 'NON_OPERATING_EXPENSE') return 'nonOperatingExpenses';
  if (type === 'TAX_EXPENSE') return 'taxExpense';
  if (type === 'FINANCE' || type === 'FINANCE_INCOME' || type === 'FINANCE_EXPENSE') {
    if (name.includes('gelir') || name.includes('income')) return 'nonOperatingIncome';
    if (name.includes('gider') || name.includes('expense')) return 'financeCosts';
    return 'financeCosts';
  }

  if (/^60[0-2]/.test(code)) return 'revenue';
  if (/^61[0-2]/.test(code)) return 'revenueDeductions';
  if (/^62[0-3]/.test(code)) return 'costOfSales';
  if (/^63[0-3]/.test(code)) return 'operatingExpenses';
  if (/^64[0-9]/.test(code)) return 'otherOperatingIncome';
  if (/^65[0-9]/.test(code)) return 'otherOperatingExpenses';
  if (/^66[0-4]/.test(code)) return 'financeCosts';
  if (/^67[0-9]/.test(code)) return 'nonOperatingIncome';
  if (/^68[0-9]/.test(code)) return 'nonOperatingExpenses';
  if (/^69[1-2]/.test(code)) return 'taxExpense';

  if (normalizedCode.includes('TAX')) return 'taxExpense';
  if (normalizedCode.includes('SALES')) return 'revenue';
  if (normalizedCode.includes('COST_OF_SALES')) return 'costOfSales';
  if (normalizedCode.includes('EXPENSE')) return 'operatingExpenses';
  if (normalizedCode.includes('INCOME')) return 'otherOperatingIncome';
  if (normalizedCode.includes('OPERATING')) return 'otherOperatingIncome';

  if (type === 'INCOME') return 'otherOperatingIncome';
  if (type === 'EXPENSE') return 'operatingExpenses';
  return null;
}

function signedAmountForBucket(line, bucket) {
  const raw = toAmount(line?.amount);
  const magnitude = Math.abs(raw);
  if (bucket === 'taxExpense') {
    return raw;
  }
  if (bucket === 'revenue' || bucket === 'otherOperatingIncome' || bucket === 'nonOperatingIncome' || bucket === 'ociItems') {
    return magnitude;
  }
  return -magnitude;
}

function combineLines(currentLines, previousLines, isTr) {
  const registry = new Map();

  currentLines.forEach((line) => {
    const key = buildLineKey(line);
    const bucket = inferBucket(line);
    if (!key || !bucket) return;
    registry.set(key, {
      key,
      code: line.code || '',
      name: displayLineName(line, isTr),
      noteRef: '',
      bucket,
      currentAmount: signedAmountForBucket(line, bucket),
      previousAmount: 0
    });
  });

  previousLines.forEach((line) => {
    const key = buildLineKey(line);
    const bucket = inferBucket(line);
    if (!key || !bucket) return;
    const existing = registry.get(key);
    if (existing) {
      existing.previousAmount = signedAmountForBucket(line, bucket);
      return;
    }
    registry.set(key, {
      key,
      code: line.code || '',
      name: displayLineName(line, isTr),
      noteRef: '',
      bucket,
      currentAmount: 0,
      previousAmount: signedAmountForBucket(line, bucket)
    });
  });

  return [...registry.values()].sort(compareRows);
}

function buildGroup(key, title, rows, variant = 'detail') {
  return {
    key,
    title,
    rows,
    variant,
    currentTotal: sumRows(rows, 'currentAmount'),
    previousTotal: sumRows(rows, 'previousAmount')
  };
}

function buildSummaryGroup(key, title, currentTotal, previousTotal, variant = 'summary') {
  return {
    key,
    title,
    rows: [],
    variant,
    currentTotal,
    previousTotal
  };
}

export function buildProfitLossOciPresentation({
  currentReport,
  previousReport,
  currentPeriodLabel,
  previousPeriodLabel,
  isTr
}) {
  const labels = isTr ? LABELS.tr : LABELS.en;
  const locale = isTr ? 'tr-TR' : 'en-US';
  const combinedRows = combineLines(currentReport?.lines || [], previousReport?.lines || [], isTr);

  const buckets = {
    revenue: [],
    revenueDeductions: [],
    costOfSales: [],
    otherOperatingIncome: [],
    operatingExpenses: [],
    otherOperatingExpenses: [],
    financeCosts: [],
    nonOperatingIncome: [],
    nonOperatingExpenses: [],
    taxExpense: [],
    ociItems: []
  };

  combinedRows.forEach((row) => {
    buckets[row.bucket].push(row);
  });

  const revenueGroup = buildGroup('revenue', labels.revenue, buckets.revenue);
  const revenueDeductionsGroup = buildGroup('revenueDeductions', labels.revenueDeductions, buckets.revenueDeductions);
  const netRevenueCurrent = revenueGroup.currentTotal + revenueDeductionsGroup.currentTotal;
  const netRevenuePrevious = revenueGroup.previousTotal + revenueDeductionsGroup.previousTotal;

  const costOfSalesGroup = buildGroup('costOfSales', labels.costOfSales, buckets.costOfSales);
  const grossProfitCurrent = netRevenueCurrent + costOfSalesGroup.currentTotal;
  const grossProfitPrevious = netRevenuePrevious + costOfSalesGroup.previousTotal;

  const otherOperatingIncomeGroup = buildGroup('otherOperatingIncome', labels.otherOperatingIncome, buckets.otherOperatingIncome);
  const operatingExpensesGroup = buildGroup('operatingExpenses', labels.operatingExpenses, buckets.operatingExpenses);
  const otherOperatingExpensesGroup = buildGroup('otherOperatingExpenses', labels.otherOperatingExpenses, buckets.otherOperatingExpenses);
  const operatingProfitCurrent =
    grossProfitCurrent
    + otherOperatingIncomeGroup.currentTotal
    + operatingExpensesGroup.currentTotal
    + otherOperatingExpensesGroup.currentTotal;
  const operatingProfitPrevious =
    grossProfitPrevious
    + otherOperatingIncomeGroup.previousTotal
    + operatingExpensesGroup.previousTotal
    + otherOperatingExpensesGroup.previousTotal;

  const financeCostsGroup = buildGroup('financeCosts', labels.financeCosts, buckets.financeCosts);
  const nonOperatingIncomeGroup = buildGroup('nonOperatingIncome', labels.nonOperatingIncome, buckets.nonOperatingIncome);
  const nonOperatingExpensesGroup = buildGroup('nonOperatingExpenses', labels.nonOperatingExpenses, buckets.nonOperatingExpenses);
  const profitBeforeTaxCurrent =
    operatingProfitCurrent
    + financeCostsGroup.currentTotal
    + nonOperatingIncomeGroup.currentTotal
    + nonOperatingExpensesGroup.currentTotal;
  const profitBeforeTaxPrevious =
    operatingProfitPrevious
    + financeCostsGroup.previousTotal
    + nonOperatingIncomeGroup.previousTotal
    + nonOperatingExpensesGroup.previousTotal;

  const taxExpenseGroup = buildGroup('taxExpense', labels.taxExpense, buckets.taxExpense);
  const profitForPeriodCurrent = profitBeforeTaxCurrent + taxExpenseGroup.currentTotal;
  const profitForPeriodPrevious = profitBeforeTaxPrevious + taxExpenseGroup.previousTotal;

  const ociItemsGroup = buildGroup('ociItems', labels.ociItems, buckets.ociItems);
  const totalOciCurrent = ociItemsGroup.currentTotal;
  const totalOciPrevious = ociItemsGroup.previousTotal;

  return {
    labels,
    locale,
    currentPeriodLabel: currentPeriodLabel || labels.currentPeriod,
    previousPeriodLabel: previousPeriodLabel || labels.previousPeriod,
    comparativeAvailable: Boolean(previousReport?.lines?.length),
    metadataNote: labels.basisNote,
    sections: [
      {
        key: 'profitLoss',
        title: labels.profitLossSection,
        groups: [
          revenueGroup,
          revenueDeductionsGroup,
          buildSummaryGroup('netRevenue', labels.netRevenue, netRevenueCurrent, netRevenuePrevious),
          costOfSalesGroup,
          buildSummaryGroup('grossProfit', labels.grossProfit, grossProfitCurrent, grossProfitPrevious),
          otherOperatingIncomeGroup,
          operatingExpensesGroup,
          otherOperatingExpensesGroup,
          buildSummaryGroup('operatingProfit', labels.operatingProfit, operatingProfitCurrent, operatingProfitPrevious, 'summary-strong'),
          financeCostsGroup,
          nonOperatingIncomeGroup,
          nonOperatingExpensesGroup,
          buildSummaryGroup('profitBeforeTax', labels.profitBeforeTax, profitBeforeTaxCurrent, profitBeforeTaxPrevious),
          taxExpenseGroup,
          buildSummaryGroup('profitForPeriod', labels.profitForPeriod, profitForPeriodCurrent, profitForPeriodPrevious, 'summary-strong')
        ],
        totalLabel: labels.totalProfitLoss,
        currentTotal: profitForPeriodCurrent,
        previousTotal: profitForPeriodPrevious
      },
      {
        key: 'oci',
        title: labels.ociSection,
        groups: [
          ociItemsGroup,
          buildSummaryGroup('totalOci', labels.totalOci, totalOciCurrent, totalOciPrevious, 'summary')
        ],
        totalLabel: labels.totalOci,
        currentTotal: totalOciCurrent,
        previousTotal: totalOciPrevious
      }
    ],
    footerRows: [
      {
        label: labels.totalComprehensiveIncome,
        current: profitForPeriodCurrent + totalOciCurrent,
        previous: profitForPeriodPrevious + totalOciPrevious,
        variant: 'total'
      }
    ]
  };
}
